import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./paymentDaysDropdown.scss";
import "../cashflowAreaDropdown/cashflowAreaDropdown.scss";

const PAYMENT_DAY_VALUES = [0, 15, 30, 45, 60, 90];

export const PaymentDaysDropdown = ({ editData, onChange }) => {
  const { t } = useTranslation("agenda");
  const formatDays = (count) => t("paymentDaysDropdown.days", { count });
  const paymentDayOptions = PAYMENT_DAY_VALUES.map((value) => ({
    key: value,
    text: formatDays(value),
    value,
  }));
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [newQuantity, setNewQuantity] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const customInputRef = useRef(null);

  useEffect(() => {
    if (editData === 0 || editData > 0) {
      const found = paymentDayOptions.find((p) => p.value === editData);
      if (found) {
        setSelectedQuantity(found);
      } else {
        setSelectedQuantity({
          key: editData,
          text: formatDays(editData),
          value: editData,
        });
      }
    }
  }, [editData, t]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const selectOption = (option) => {
    setSelectedQuantity(option);
    setNewQuantity("");
    onChange(option.value);
    setIsOpen(false);
  };

  const selectNewQuantity = () => {
    const val = newQuantity?.trim();
    if (val) {
      const num = parseFloat(val.replace(/[^0-9Kk]/g, ""));
      if (!isNaN(num)) {
        selectOption({
          key: num,
          text: formatDays(num),
          value: num,
        });
      }
    }
  };

  const selectedText = selectedQuantity?.text || "";
  const showPlaceholder = !selectedText && !isOpen;

  return (
    <div
      ref={dropdownRef}
      className={`cashflow-area-dropdown payment-days-dropdown ${isOpen ? "open" : ""}`}
    >
      <div className="dropdown-wrapper">
        <input
          ref={triggerRef}
          type="text"
          className="dropdown-input"
          value={selectedText}
          readOnly
          placeholder={showPlaceholder ? t("paymentDaysDropdown.selectDays") : ""}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        />
        <span className="dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path
              stroke="#6b7280"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="m6 8 4 4 4-4"
            />
          </svg>
        </span>
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          <ul className="dropdown-options" role="listbox">
            {paymentDayOptions.map((option) => (
              <li
                key={option.key}
                className={`dropdown-option ${
                  selectedQuantity?.value === option.value ? "selected" : ""
                }`}
                onClick={() => selectOption(option)}
                role="option"
                aria-selected={selectedQuantity?.value === option.value}
              >
                <span className="option-text">{option.text}</span>
              </li>
            ))}
          </ul>
          <div className="add-area-section">
            <div className="add-area-header">{t("paymentDaysDropdown.other")}</div>
            <div className="add-area-items">
              <input
                ref={customInputRef}
                className="add-area-input"
                placeholder={t("paymentDaysDropdown.daysPlaceholder")}
                type="text"
                value={newQuantity}
                onChange={(e) =>
                  setNewQuantity(e.target.value.replace(/[^0-9Kk]/g, ""))
                }
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.key === "Enter" && selectNewQuantity()}
              />
              <button
                className="add-area-button add-area-button--text"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  selectNewQuantity();
                }}
              >
                {t("paymentDaysDropdown.enter")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
